import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Toolbar,
  Typography,
  Button,
  Box,
  Container,
  Menu,
  MenuItem,
  IconButton,
  Badge,
  ListItemText,
  Divider,
  ListItemIcon,
  Drawer,
  List,
  ListItem,
  ListItemButton,
} from '@mui/material';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import MailIcon from '@mui/icons-material/Mail';
import NotificationsIcon from '@mui/icons-material/Notifications';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MenuIcon from '@mui/icons-material/Menu';
import { useAuth } from '../contexts/AuthContext';
import { messagesAPI, notificationsAPI, Notification } from '../api';

const Navbar: React.FC = () => {
  const { user, isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [notifAnchorEl, setNotifAnchorEl] = React.useState<null | HTMLElement>(null);
  const [unreadMessagesCount, setUnreadMessagesCount] = useState(0);
  const [unreadNotifCount, setUnreadNotifCount] = useState(0);
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Fetch unread counts when authenticated
  useEffect(() => {
    if (isAuthenticated) {
      loadUnreadCounts();
      loadNotifications();
      // Poll for updates every 30 seconds
      const interval = setInterval(() => {
        loadUnreadCounts();
        loadNotifications();
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isAuthenticated]);

  const loadUnreadCounts = async () => {
    try {
      const [messagesData, notifsData] = await Promise.all([
        messagesAPI.getUnreadCount(),
        notificationsAPI.getUnreadCount(),
      ]);
      setUnreadMessagesCount(messagesData.count);
      setUnreadNotifCount(notifsData.count);
    } catch (err) {
      console.error('Failed to load unread counts:', err);
    }
  };

  const loadNotifications = async () => {
    try {
      const data = await notificationsAPI.getAll({ limit: 5 });
      setNotifications(data.notifications);
    } catch (err) {
      console.error('Failed to load notifications:', err);
    }
  };

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    logout();
    handleClose();
    setMobileOpen(false);
    navigate('/');
  };

  const handleNotifMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setNotifAnchorEl(event.currentTarget);
  };

  const handleNotifMenuClose = () => {
    setNotifAnchorEl(null);
  };

  const handleNotificationClick = async (notification: Notification) => {
    if (!notification.isRead) {
      try {
        await notificationsAPI.markAsRead(notification.id);
        loadUnreadCounts();
        loadNotifications();
      } catch (err) {
        console.error('Failed to mark notification as read:', err);
      }
    }
    if (notification.actionUrl) {
      navigate(notification.actionUrl);
    }
    handleNotifMenuClose();
  };

  const handleMarkAllRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      loadUnreadCounts();
      loadNotifications();
    } catch (err) {
      console.error('Failed to mark all as read:', err);
    }
  };

  // Nav items for both desktop buttons and mobile drawer
  const navItems = [
    { label: 'Teams', to: '/teams' },
    { label: 'Discover', to: '/discover' },
    { label: 'Players', to: '/players' },
    { label: 'Posts', to: '/posts' },
    { label: 'Tryouts', to: '/tryouts' },
    ...(isAuthenticated && user?.role === 'parent' ? [{ label: '🏠 Portal', to: '/parent-portal' }] : []),
    ...(isAuthenticated ? [
      { label: 'Messages', to: '/messages', badge: unreadMessagesCount },
      { label: 'Profile', to: '/profile' },
    ] : []),
  ];

  const mobileDrawer = (
    <Drawer
      anchor="left"
      open={mobileOpen}
      onClose={() => setMobileOpen(false)}
      sx={{ display: { xs: 'block', md: 'none' } }}
      PaperProps={{ sx: { width: 280 } }}
    >
      <Box onClick={() => setMobileOpen(false)}>
        <Box sx={{ p: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box component="img" src="/favicon.svg" alt="" sx={{ width: 26, height: 26, borderRadius: 1.2 }} />
          <Typography
            variant="h6"
            sx={{
              fontWeight: 800,
              fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif',
              textTransform: 'uppercase',
              letterSpacing: '0.05em',
            }}
          >
            Squadhatch
          </Typography>
        </Box>
        <Divider />
        {isAuthenticated && user && (
          <>
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="body2" fontWeight={600}>{user.firstName} {user.lastName}</Typography>
              <Typography variant="caption" color="text.secondary">{user.email}</Typography>
            </Box>
            <Divider />
          </>
        )}
        <List>
          {navItems.map((item) => (
            <ListItem key={item.to} disablePadding>
              <ListItemButton component={RouterLink} to={item.to}>
                <ListItemText
                  primary={
                    'badge' in item && item.badge ? `${item.label} (${item.badge})` : item.label
                  }
                />
              </ListItemButton>
            </ListItem>
          ))}
        </List>
        {!isAuthenticated && (
          <>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/login">
                  <ListItemText primary="Login" />
                </ListItemButton>
              </ListItem>
              <ListItem disablePadding>
                <ListItemButton component={RouterLink} to="/register">
                  <ListItemText primary="Sign Up" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
        {isAuthenticated && (
          <>
            <Divider />
            <List>
              <ListItem disablePadding>
                <ListItemButton onClick={handleLogout}>
                  <ListItemText primary="Logout" />
                </ListItemButton>
              </ListItem>
            </List>
          </>
        )}
      </Box>
    </Drawer>
  );

  return (
    <>
      <AppBar position="static">
        <Container maxWidth="lg">
          <Toolbar disableGutters>
            {/* Hamburger — mobile only */}
            <IconButton
              color="inherit"
              onClick={() => setMobileOpen(true)}
              sx={{ display: { xs: 'flex', md: 'none' }, mr: 1 }}
            >
              <MenuIcon />
            </IconButton>

            <Box component="img" src="/favicon.svg" alt="" sx={{ width: 30, height: 30, mr: 1.2, borderRadius: 1.5 }} />
            <Typography
              variant="h6"
              component={RouterLink}
              to="/"
              sx={{
                mr: 4,
                textDecoration: 'none',
                color: 'inherit',
                fontWeight: 800,
                fontFamily: '"Barlow Condensed", "Arial Narrow", sans-serif',
                textTransform: 'uppercase',
                letterSpacing: '0.05em',
                fontSize: '1.45rem',
              }}
            >
              Squadhatch
            </Typography>

            {/* Desktop nav — hidden on mobile */}
            <Box sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' }, gap: 2 }}>
              <Button color="inherit" component={RouterLink} to="/teams">Teams</Button>
              <Button color="inherit" component={RouterLink} to="/discover">Discover</Button>
              <Button color="inherit" component={RouterLink} to="/players">Players</Button>
              <Button color="inherit" component={RouterLink} to="/posts">Posts</Button>
              <Button color="inherit" component={RouterLink} to="/tryouts">Tryouts</Button>
              {isAuthenticated && user?.role === 'parent' && (
                <Button color="inherit" component={RouterLink} to="/parent-portal">🏠 Portal</Button>
              )}
              {isAuthenticated && user?.role === 'team_manager' && (
                <Button
                  variant="contained"
                  color="warning"
                  size="small"
                  component={RouterLink}
                  to="/subscription"
                  sx={{ fontWeight: 700, ml: 0.5 }}
                >
                  ⚡ Upgrade
                </Button>
              )}
              {isAuthenticated && (
                <>
                  <Button
                    color="inherit"
                    component={RouterLink}
                    to="/messages"
                    startIcon={
                      <Badge badgeContent={unreadMessagesCount} color="error">
                        <MailIcon />
                      </Badge>
                    }
                  >
                    Messages
                  </Button>
                  <Button color="inherit" component={RouterLink} to="/profile">Profile</Button>
                </>
              )}
            </Box>

            {/* Spacer on mobile so right-side icons stay right */}
            <Box sx={{ flexGrow: 1, display: { xs: 'flex', md: 'none' } }} />

            {/* Right-side icons — always visible */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              {isAuthenticated && user ? (
                <>
                  {/* Notifications Bell */}
                  <IconButton size="large" color="inherit" onClick={handleNotifMenuOpen}>
                    <Badge badgeContent={unreadNotifCount} color="error">
                      <NotificationsIcon />
                    </Badge>
                  </IconButton>
                  <Menu
                    anchorEl={notifAnchorEl}
                    open={Boolean(notifAnchorEl)}
                    onClose={handleNotifMenuClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                    PaperProps={{ sx: { width: 350, maxHeight: 400 } }}
                  >
                    <MenuItem disabled>
                      <Typography variant="subtitle2" fontWeight="bold">Notifications</Typography>
                    </MenuItem>
                    <Divider />
                    {notifications.length === 0 ? (
                      <MenuItem disabled>
                        <Typography variant="body2" color="text.secondary">No notifications</Typography>
                      </MenuItem>
                    ) : (
                      <>
                        {notifications.map((notif) => (
                          <MenuItem
                            key={notif.id}
                            onClick={() => handleNotificationClick(notif)}
                            sx={{
                              backgroundColor: notif.isRead ? 'transparent' : 'action.hover',
                              whiteSpace: 'normal',
                              py: 1.5,
                            }}
                          >
                            <ListItemText
                              primary={notif.title}
                              secondary={notif.message}
                              secondaryTypographyProps={{ sx: { fontSize: '0.8rem' } }}
                            />
                          </MenuItem>
                        ))}
                        <Divider />
                        <MenuItem onClick={handleMarkAllRead}>
                          <ListItemIcon><MarkEmailReadIcon fontSize="small" /></ListItemIcon>
                          <ListItemText primary="Mark all as read" />
                        </MenuItem>
                        <MenuItem onClick={() => { navigate('/notifications'); handleNotifMenuClose(); }}>
                          <ListItemText primary="View all notifications" />
                        </MenuItem>
                      </>
                    )}
                  </Menu>

                  {/* Username — hidden on small screens */}
                  <Typography variant="body2" sx={{ display: { xs: 'none', sm: 'block' }, mr: 1 }}>
                    {user.firstName} {user.lastName}
                  </Typography>

                  <IconButton size="large" onClick={handleMenu} color="inherit">
                    <AccountCircleIcon />
                  </IconButton>
                  <Menu
                    anchorEl={anchorEl}
                    open={Boolean(anchorEl)}
                    onClose={handleClose}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                    transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                  >
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">{user.email}</Typography>
                    </MenuItem>
                    <MenuItem disabled>
                      <Typography variant="body2" color="text.secondary">Role: {user.role}</Typography>
                    </MenuItem>
                    <MenuItem onClick={handleLogout}>Logout</MenuItem>
                  </Menu>
                </>
              ) : (
                <>
                  <Button color="inherit" component={RouterLink} to="/login">Login</Button>
                  <Button
                    variant="outlined"
                    color="inherit"
                    component={RouterLink}
                    to="/register"
                    sx={{ display: { xs: 'none', sm: 'flex' } }}
                  >
                    Sign Up
                  </Button>
                </>
              )}
            </Box>
          </Toolbar>
        </Container>
      </AppBar>

      {mobileDrawer}
    </>
  );
};

export default Navbar;
